import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Mail,
  Phone,
  LogOut,
  RefreshCw,
  Calendar,
  User,
  Shield,
  FileText
} from 'lucide-react';

const PendingApproval = () => {
  const { user, logout, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState('');
  const [lastChecked, setLastChecked] = useState('');
  const navigate = useNavigate();

  // Calculate time elapsed since registration
  useEffect(() => {
    if (user?.createdAt) {
      const startTime = new Date(user.createdAt);
      const updateTimeElapsed = () => {
        const now = new Date();
        const diff = now - startTime;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) {
          setTimeElapsed(`${days} ngày ${hours} giờ`);
        } else if (hours > 0) {
          setTimeElapsed(`${hours} giờ ${minutes} phút`);
        } else {
          setTimeElapsed(`${minutes} phút`);
        }
      };
      
      updateTimeElapsed();
      const interval = setInterval(updateTimeElapsed, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [user?.createdAt]);

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = async () => {
    if (isLoading) return; // Prevent multiple clicks
    
    setIsLoading(true);
    toast.loading('Đang kiểm tra trạng thái phê duyệt...', { id: 'refresh-status' });
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Không tìm thấy token. Vui lòng đăng nhập lại.', { id: 'refresh-status' });
        setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, 2000);
        return;
      }

      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Call real API endpoint /auth/me
      const response = await api.get('/auth/me');
      
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        const userData = response.data.data;
        const userStatus = userData.status;
        
        console.log('User status check:', {
          id: userData.id,
          email: userData.email,
          role: userData.role,
          status: userStatus
        });
        
        if (userStatus === 'approved') {
          // User is approved - redirect based on role
          let redirectPath = '/login';
          let roleName = '';
          
          // Redirect based on user role
          switch (userData.role) {
            case 'admin':
              navigate('/admin');
              roleName = 'Admin Dashboard';
              break;
            case 'seller':
              navigate('/seller');
              roleName = 'Seller Dashboard';
              break;
            case 'designer':
              navigate('/designer');
              roleName = 'Designer Dashboard';
              break;
            default:
              navigate('/login');
              roleName = '';
          }
          
          toast.success(`Tài khoản của bạn đã được phê duyệt! Chuyển hướng đến ${roleName}...`, { id: 'refresh-status' });
          
          // Log approval
          console.log('User approved:', {
            id: userData.id,
            email: userData.email,
            role: userData.role
          });
          
          // Redirect after approval
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 1500);
          
        } else if (userStatus === 'rejected') {
          // User is rejected
          toast.error('Tài khoản của bạn đã bị từ chối. Vui lòng liên hệ admin để biết thêm chi tiết.', { id: 'refresh-status' });
          
          // Log rejection
          console.log('User rejected:', {
            id: userData.id,
            email: userData.email,
            role: userData.role
          });
          
          // Show rejection reason
          setTimeout(() => {
            toast.error('Lý do: Không đủ điều kiện hoặc thông tin không hợp lệ', { duration: 5000 });
          }, 1000);
          
          // Redirect to login page after rejection
          setTimeout(() => {
            logout();
            window.location.href = '/login';
          }, 3000);
          
        } else if (userStatus === 'pending') {
          // User is still pending
          toast.success('Trạng thái: Vẫn đang chờ phê duyệt. Vui lòng kiểm tra lại sau.', { id: 'refresh-status' });
          
          // Log pending status
          console.log('User still pending:', {
            id: userData.id,
            email: userData.email,
            role: userData.role
          });
          
          // Show estimated time
          setTimeout(() => {
            toast.success('Thời gian ước tính: 1-24 giờ làm việc', { duration: 4000 });
          }, 1000);
        }
        
      } else {
        // API returned error
        toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
        console.error('API Error:', response.data);
      }
      
      // Update last checked time
      const now = new Date();
      setLastChecked(now.toLocaleTimeString('vi-VN'));
      
    } catch (error) {
      console.error('Refresh error:', error);
      
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        if (status === 401) {
          // Unauthorized - token expired or invalid
          toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.', { id: 'refresh-status' });
          setTimeout(() => {
            logout();
            window.location.href = '/login';
          }, 2000);
        } else if (status === 403) {
          // Forbidden
          toast.error('Không có quyền truy cập. Vui lòng liên hệ admin.', { id: 'refresh-status' });
        } else {
          toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
        }
      } else if (error.request) {
        // Network error
        toast.error('Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.', { id: 'refresh-status' });
      } else {
        // Other error
        toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading if auth is still loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-600">MAIN_SYS</h1>
              </div>
            </div>
            {user && (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-2 sm:px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <LogOut className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Đăng xuất</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-lg w-full">
          <div className="text-center">
            {/* Status Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100 mb-6 shadow-lg">
              <Clock className="h-10 w-10 text-yellow-600" />
            </div>

            {/* Status Title */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Tài khoản đang chờ duyệt
            </h2>

            {/* Status Message */}
            <p className="text-gray-600 mb-8 text-sm sm:text-base leading-relaxed">
              Cảm ơn bạn đã đăng ký! Tài khoản của bạn đang được admin xem xét và phê duyệt. 
              Quá trình này thường mất 24 giờ làm việc.
            </p>

            {/* Time Elapsed */}
            {timeElapsed && (
              <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                <div className="flex items-center justify-center space-x-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    Đã chờ: {timeElapsed}
                  </span>
                </div>
              </div>
            )}

            {/* User Info Card */}
            {user ? (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-200">
                <div className="flex items-center mb-4">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="h-12 w-12 rounded-full"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-lg font-medium text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    {user?.role && (
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <User className="h-3 w-3 mr-1" />
                          {user.role === 'seller' ? 'Người bán' : 
                           user.role === 'designer' ? 'Thiết kế' : 
                           user.role === 'admin' ? 'Quản trị viên' : 'Người dùng'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                      <span>Trạng thái: Chờ phê duyệt</span>
                    </div>
                    <button
                      onClick={handleRefresh}
                      disabled={isLoading}
                      className="flex items-center px-3 py-1 text-sm font-medium text-blue-600 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                      {isLoading ? 'Đang kiểm tra...' : 'Làm mới'}
                    </button>
                  </div>
                  {lastChecked && (
                    <div className="mt-2 text-xs text-gray-500">
                      Lần kiểm tra cuối: {lastChecked}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-200">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Không có thông tin người dùng</h3>
                  <p className="text-sm text-gray-500">Vui lòng đăng nhập để xem thông tin</p>
                </div>
              </div>
            )}

            {/* What happens next */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6 border border-blue-200">
              <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Điều gì sẽ xảy ra tiếp theo?
              </h3>
              <div className="space-y-3 text-sm text-blue-800">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>Admin sẽ xem xét thông tin tài khoản của bạn</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>Bạn sẽ nhận được email thông báo khi được phê duyệt</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>Sau khi được phê duyệt, bạn có thể truy cập đầy đủ hệ thống</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>Thời gian xử lý thường từ 1-24 giờ làm việc</span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Cần hỗ trợ?
              </h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-3 text-gray-400" />
                  <span>Email: support@mainsys.com</span>
                </div>
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-3 text-gray-400" />
                  <span>Điện thoại: +84 123 456 789</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-500 leading-relaxed">
                Nếu bạn cần hỗ trợ khẩn cấp hoặc có thắc mắc về quá trình phê duyệt, 
                vui lòng liên hệ với chúng tôi qua email hoặc điện thoại.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            <p>&copy; 2024 MAIN_SYS. Tất cả quyền được bảo lưu.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PendingApproval; 